import React, { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import he from "he";
import ChallengeImage from "../../components/ChallengeImage";
import StageTabs, { useAdminTheme } from "../../components/StageTabs";
import { assetUrl } from "../../../functions/themeAssets";
import { deletePair, getAdminPairs, imageSize, savePair, updatePairs } from "../../functions/stageApi";
import "../editor/editor.css";

const VALID_TYPES = ["image/jpeg", "image/png", "image/jpg"];

/** Stage 2 — question / answer pairs of "Think Simple" (addquestions.php). */
export default function Pairs() {
  const { adminToken, currentTheme, data, save } = useAdminTheme();
  const [pairs, setPairs] = useState([]);
  const [edits, setEdits] = useState({}); // id -> { item1, item2, file }
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [total, setTotal] = useState("");
  const [busy, setBusy] = useState(false);

  // add modal
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("text");
  const [item1, setItem1] = useState("");
  const [item2, setItem2] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const json = await getAdminPairs({ token: adminToken, themeName: currentTheme });
      setPairs(json.pairs || []);
      setEdits({});
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [currentTheme, adminToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data) return;
    setTitle(String(data.landing_page_title2 ?? ""));
    const n = parseInt(data.questions_02, 10);
    setTotal(n > 0 ? String(n) : "");
  }, [data]);

  const edit = (id, patch) => setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const onSaveSettings = async () => {
    const t = title.trim();
    const n = parseInt(total, 10);
    let problem = "";
    if (t.length === 0 || t.length > 150) problem = t.length === 0 ? "The title cannot be empty or contain only spaces." : "You exceeded the maximum character limit for the title.";
    else if (String(total).trim() === "") problem = "Please enter total questions.";
    else if (Number.isNaN(n) || n < 1) problem = "Total questions must be at least 1.";
    else if (n > 10) problem = "Total questions cannot be more than 10.";
    else if (n > pairs.length) problem = `You cannot enter more than ${pairs.length} questions.`;
    if (problem) return Swal.fire({ icon: "error", title: "Validation Error", text: problem });

    try {
      await save({ questions_02: n, landing_page_title2: title });
      Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const onDelete = async (pair) => {
    const r = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (!r.isConfirmed) return;
    try {
      await deletePair({ token: adminToken, id: pair.id });
      await Swal.fire({ title: "Deleted!", text: "Your record has been deleted.", icon: "success", timer: 1200 });
      load();
    } catch (e) {
      Swal.fire("Error!", e.message, "error");
    }
  };

  const checkImage = async (f) => {
    if (!VALID_TYPES.includes(f.type)) throw new Error("Only JPG, JPEG, or PNG images are allowed.");
    const { width, height } = await imageSize(f);
    if (width !== 400 || height !== 150) throw new Error("Image must be exactly 400 x 150 pixels.");
  };

  const onPickEditImage = async (id, f, input) => {
    if (!f) return edit(id, { file: null });
    try {
      await checkImage(f);
      edit(id, { file: f });
    } catch (e) {
      input.value = "";
      Swal.fire("", e.message, "error");
    }
  };

  const onUpdateAll = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("themeName", currentTheme);
    const list = pairs.map((p) => {
      const ed = edits[p.id] || {};
      if (ed.file) fd.append(`image_${p.id}`, ed.file);
      return {
        id: p.id,
        ...(p.isImage ? {} : { item1: ed.item1 !== undefined ? ed.item1 : he.decode(p.item1) }),
        item2: ed.item2 !== undefined ? ed.item2 : p.item2,
      };
    });
    fd.append("pairs", JSON.stringify(list));
    setBusy(true);
    try {
      await updatePairs({ token: adminToken, formData: fd });
      await Swal.fire({ icon: "success", title: "Success", text: "Update pairs successfully!" });
      load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setType("text");
    setItem1("");
    setItem2("");
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickNewImage = async (f, input) => {
    if (!f) return setFile(null);
    try {
      await checkImage(f);
      setFile(f);
    } catch (e) {
      input.value = "";
      setFile(null);
      Swal.fire("", e.message, "error");
    }
  };

  const onAdd = async () => {
    if (type === "text" && !item1.trim()) return Swal.fire("", "Please enter a valid Question.", "error");
    if (type === "image" && !file) return Swal.fire("", "Please upload an image.", "error");
    if (!item2.trim()) return Swal.fire("", "Please enter a valid Answer.", "error");
    if (item1.length > 80) return Swal.fire("", "Question should not exceed 80 characters.", "error");
    if (item2.length > 80) return Swal.fire("", "Answer should not exceed 80 characters.", "error");

    const fd = new FormData();
    fd.append("themeName", currentTheme);
    fd.append("type", type);
    fd.append("item1", item1);
    fd.append("item2", item2);
    if (type === "image") fd.append("image", file);
    setBusy(true);
    try {
      await savePair({ token: adminToken, formData: fd });
      closeModal();
      await Swal.fire({ icon: "success", title: "Success", text: "Pairs added successfully!" });
      load();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <StageTabs />

      <div className="bp-card">
        <div className="bp-row">
          <div className="bp-col">
            <h4>Add Custom Heading Title For Challenge 2:</h4>
            <textarea className="bp-textarea" maxLength={150} value={title} onChange={(e) => setTitle(e.target.value)} />
            <p className="bp-note">* This title will reflect on Challenge 2 of game page &nbsp; * Character limit of text is 150</p>
          </div>
          <div className="bp-col">
            <h4>Total Questions</h4>
            <input
              type="number"
              className="bp-input"
              min="1"
              max="10"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
            <p className="bp-note">* Total number of questions to be display</p>
          </div>
        </div>
        <div className="bp-actions">
          <button type="button" className="bp-btn" onClick={onSaveSettings}>
            Save
          </button>
        </div>
      </div>

      <ChallengeImage stage={2} />

      <form className="bp-card" style={{ maxWidth: 1200 }} onSubmit={onUpdateAll}>
        {loading ? (
          <p>Loading...</p>
        ) : pairs.length === 0 ? (
          <p>No questions yet. Click Add to create one.</p>
        ) : (
          <div className="bp-pairs">
            {pairs.map((pair, i) => {
              const ed = edits[pair.id] || {};
              return (
                <div className="bp-pair" key={pair.id}>
                  <button type="button" className="bp-del" onClick={() => onDelete(pair)}>
                    &times;
                  </button>
                  <h6>Question {i + 1}:</h6>
                  {pair.isImage ? (
                    <>
                      <img className="bp-thumb" src={assetUrl(pair.item1)} alt="" />
                      <input
                        type="file"
                        accept="image/*"
                        className="bp-input"
                        onChange={(e) => onPickEditImage(pair.id, e.target.files[0], e.target)}
                      />
                    </>
                  ) : (
                    <input
                      type="text"
                      className="bp-input"
                      placeholder="Edit Question"
                      value={ed.item1 !== undefined ? ed.item1 : he.decode(pair.item1)}
                      onChange={(e) => edit(pair.id, { item1: e.target.value })}
                    />
                  )}
                  <h6>Answer:</h6>
                  <input
                    type="text"
                    className="bp-input"
                    placeholder="Edit Answer"
                    value={ed.item2 !== undefined ? ed.item2 : pair.item2}
                    onChange={(e) => edit(pair.id, { item2: e.target.value })}
                  />
                </div>
              );
            })}
          </div>
        )}
        <div className="bp-actions">
          <button type="submit" className="bp-btn" disabled={busy || pairs.length === 0}>
            <i className="fa-solid fa-cloud-arrow-up" /> Update All
          </button>
          <button type="button" className="bp-btn" onClick={() => setOpen(true)}>
            <i className="fa-solid fa-plus" /> Add
          </button>
        </div>
      </form>

      {open && (
        <div className="qe-backdrop" onClick={closeModal}>
          <div className="qe-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Add New Content</h4>

            <label>Select the type of question</label>
            <select className="bp-input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="text">Text</option>
              <option value="image">Image</option>
            </select>

            {type === "text" ? (
              <>
                <input
                  type="text"
                  className="bp-input"
                  style={{ marginTop: 10 }}
                  maxLength={50}
                  placeholder="Enter Question"
                  value={item1}
                  onChange={(e) => setItem1(e.target.value)}
                />
                <div className="qe-note">* The text limit is set to 50 characters.</div>
              </>
            ) : (
              <>
                <input
                  type="file"
                  ref={fileRef}
                  accept=".png,.jpg,.jpeg"
                  className="bp-input"
                  style={{ marginTop: 10 }}
                  onChange={(e) => onPickNewImage(e.target.files[0], e.target)}
                />
                <div className="qe-note">* Allowed formats: PNG, JPG, JPEG &nbsp; * Image size should be 400px by 150px.</div>
              </>
            )}

            <label style={{ marginTop: 10 }}>Answer</label>
            <input
              type="text"
              className="bp-input"
              maxLength={30}
              placeholder="Enter Answer"
              value={item2}
              onChange={(e) => setItem2(e.target.value)}
            />
            <div className="qe-note">* The text limit is set to 30 characters.</div>

            <div className="qe-modal-actions">
              <button type="button" className="qe-btn qe-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="button" className="qe-btn" onClick={onAdd} disabled={busy}>
                {busy ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
